import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { campaignApi } from '../services/api';
import { Campaign, CampaignFilters } from '../types';

export default function CampaignDashboard() {
  const [filters, setFilters] = useState<CampaignFilters>({});

  const { data: campaigns = [], isLoading, error, refetch } = useQuery({
    queryKey: ['campaigns', filters],
    queryFn: () => campaignApi.getCampaigns(filters),
  });

  const handleFilterChange = (key: keyof CampaignFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const getStatusBadge = (status: Campaign['status']) => {
    const statusClass = `campaign-status status-${status}`;
    return <span className={statusClass}>{status.toUpperCase()}</span>;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  if (isLoading) {
    return <div className="loading">Loading campaigns...</div>;
  }

  if (error) {
    return (
      <div className="error">
        Error loading campaigns: {error instanceof Error ? error.message : 'Unknown error'}
        <button onClick={() => refetch()} className="btn btn-secondary" style={{ marginLeft: '1rem' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Campaign Dashboard</h1>
        <div>
          <span>{campaigns.length} campaigns</span>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="complete">Complete</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="search-filter">Search</label>
          <input
            id="search-filter"
            type="text"
            placeholder="Campaign name..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="date-from">From Date</label>
          <input
            id="date-from"
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="date-to">To Date</label>
          <input
            id="date-to"
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </div>

        <button
          onClick={() => setFilters({})}
          className="btn btn-secondary"
          style={{ alignSelf: 'flex-end' }}
        >
          Clear Filters
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="loading">No campaigns found</div>
      ) : (
        <div className="campaign-grid">
          {campaigns.map((campaign) => (
            <div key={campaign.campaignId} className="campaign-card">
              <h3>
                <Link to={`/campaigns/${campaign.campaignId}`}>
                  {campaign.name}
                </Link>
              </h3>
              <div style={{ marginBottom: '0.5rem' }}>
                {getStatusBadge(campaign.status)}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                <div>Card Size: {campaign.cardSize}</div>
                <div>Paper: {campaign.paperStock}</div>
                <div>Created: {formatDate(campaign.createdAt)}</div>
                {campaign.updatedAt !== campaign.createdAt && (
                  <div>Updated: {formatDate(campaign.updatedAt)}</div>
                )}
                {campaign.recipientCount && (
                  <div>Recipients: {campaign.recipientCount}</div>
                )}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <Link 
                  to={`/campaigns/${campaign.campaignId}`}
                  className="btn btn-primary"
                  style={{ fontSize: '0.75rem' }}
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}